import { Spinner } from "@heroui/react";
import type { ReactNode } from "react";

type AssetThumbnailProps = {
	imageUrl: string | null;
	videoUrl?: string | null;
	isLoading?: boolean;
	label?: string;
	caption?: string;
	placeholder?: string;
	badge?: ReactNode;
	overlay?: ReactNode;
	selected?: boolean;
	aspect?: "portrait" | "square" | "video";
	onPress?: () => void;
};

const ASPECT_CLASS: Record<NonNullable<AssetThumbnailProps["aspect"]>, string> = {
	portrait: "aspect-[9/16]",
	square: "aspect-square",
	video: "aspect-video",
};

const AssetThumbnail: React.FC<AssetThumbnailProps> = ({
	imageUrl,
	videoUrl,
	isLoading,
	label,
	caption,
	placeholder,
	badge,
	overlay,
	selected,
	aspect = "portrait",
	onPress,
}) => {
	const frameClass = `group relative flex ${ASPECT_CLASS[aspect]} w-full items-center justify-center overflow-hidden rounded-xl border bg-glass ${
		selected ? "border-accent-lav" : "border-border-soft"
	} ${onPress ? "cursor-pointer transition hover:border-accent-lav" : ""}`;

	const media = videoUrl ? (
		<video
			src={videoUrl}
			poster={imageUrl ?? undefined}
			controls
			muted
			playsInline
			preload="metadata"
			className="size-full bg-background object-cover"
		/>
	) : imageUrl ? (
		<img src={imageUrl} alt={label ?? ""} className="size-full bg-background object-cover" />
	) : (
		<span className="px-3 text-center text-muted text-xs">{placeholder ?? "À générer"}</span>
	);

	return (
		<div className="flex flex-col gap-1.5">
			{onPress ? (
				<button type="button" onClick={onPress} className={frameClass}>
					{media}
					{isLoading ? (
						<span className="absolute inset-0 flex items-center justify-center bg-background/60">
							<Spinner size="sm" />
						</span>
					) : null}
					{badge ? <span className="absolute top-1.5 left-1.5">{badge}</span> : null}
					{overlay}
				</button>
			) : (
				<div className={frameClass}>
					{media}
					{isLoading ? (
						<span className="absolute inset-0 flex items-center justify-center bg-background/60">
							<Spinner size="sm" />
						</span>
					) : null}
					{badge ? <span className="absolute top-1.5 left-1.5">{badge}</span> : null}
					{overlay}
				</div>
			)}
			{label ? <span className="truncate font-medium text-foreground text-xs">{label}</span> : null}
			{caption ? <span className="truncate text-muted text-xs">{caption}</span> : null}
		</div>
	);
};

export default AssetThumbnail;
